import {makeAutoObservable, reaction} from 'mobx';
import {bookingStore} from '../../store/booking-store';
import {
  BookingCreateParams,
  BookingDetail,
  BookingListItem,
  BookingModel,
  BookingStatus,
  DateRangeFilter,
  DateRangeMode
} from '../../model/professional/booking-model';
import { format, startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns';
import toastStore from '../../store/toast-store';
import availabilityStore from '../../store/availability-store';
import {Availability} from '../../model/professional/availability-model';
import {formatDateInTimeZone, formatTimeInTimeZone, utcToLocalTime} from '../../utils/timezone';
import {HTTP_STATUS_CODE} from "../../utils/UHttp";
import {useCalendarViewModel} from "./calendar-view-model";
import {useDashboardViewModel} from "./dashboard-view-model";
import companyStore from "../../store/company-store";
import professionalStore from "../../store/professional-store";

type ViewMode = 'list' | 'grid';

/**
 * ViewModel pour les réservations qui utilise l'approche hybride
 * Combinant un view-model observable avec un store séparé
 */
class BookingViewModelMobX {
  isLoading: boolean = false;
  error: string | null = null;
  protected bookingModel: BookingModel;
  calendarViewModel = useCalendarViewModel()
  dashboardViewModel = useDashboardViewModel()
  collaboratorId: string | undefined = undefined
  selectedCollaboratorId: string = '';

  constructor() {
    makeAutoObservable(this);
    this.bookingModel = BookingModel.getInstance();

    reaction(
      () => this.selectedCollaboratorId,
      (collaboratorId) => {
        if (collaboratorId) {
          this.fetchProfessionalBookings(this.activeFilter);
        }
      }
    );

    reaction(
        // Fonction de transformation (data function)
        () => {
          return {
            dateRangeMode: this.dateRangeMode,
            selectedDate: this.selectedDate,
            activeFilter: this.activeFilter
          };
        },
        // Fonction d'effet (effect function)
        (data) => {
          this.fetchProfessionalBookings(data.activeFilter).then();
        }
    )
  }

  activeFilter: 'confirmed' | 'cancelled' | 'completed' | 'all' = 'all';
  searchTerm: string = '';
  dateRangeMode: DateRangeMode = 'day';
  selectedDate: Date = new Date();
  viewMode: ViewMode = 'list';
  selectedReservations: string[] = [];

  showDetailsId: string | null = null;
  setCollaboratorId (collaboratorId: string  ) {
    this.collaboratorId = String(collaboratorId);
  }

  setLoading(isLoading: boolean) {
    this.isLoading = isLoading;
  }

  setError(error: string | null) {
    this.error = error;
  }

  setActiveFilter = (filter: BookingStatus) => {
    this.activeFilter = filter;
  }

  setSearchTerm = (term: string) => {
    this.searchTerm = term;
  }

  setDateRangeMode = (mode: DateRangeMode) => {
    this.dateRangeMode = mode;
  }

  setSelectedDate = (date: Date) => {
    this.selectedDate = date;
  }

  setViewMode = (mode: ViewMode) => {
    this.viewMode = mode;
  }

  getDateRangeFilter = (): DateRangeFilter => {
    const date = this.selectedDate;
    let start: Date;
    let end: Date;

    switch (this.dateRangeMode) {
      case 'day':
        start = startOfDay(date);
        end = endOfDay(date);
        break;
      case 'week':
        start = startOfWeek(date, { weekStartsOn: 1 }); // Lundi
        end = endOfWeek(date, { weekStartsOn: 1 }); // Dimanche
        break;
      case 'month':
        start = startOfMonth(date);
        end = endOfMonth(date);
        break;
      default:
        start = startOfDay(date);
        end = endOfDay(date);
    }

    return {
      startDate: format(start, 'yyyy-MM-dd'),
      endDate: format(end, 'yyyy-MM-dd'),
      mode: this.dateRangeMode
    };
  }

  setSelectedReservations = (reservations: string[]) => {
    this.selectedReservations = reservations;
  }

  setShowDetailsId = (id: string | null) => {
    this.showDetailsId = id;
  }

  setSelectedCollaboratorId(id: string): void {
    this.selectedCollaboratorId = id;
  }

  /**
   * Récupère toutes les réservations du professionnel
   */
  fetchProfessionalBookings = async (status: 'confirmed' | 'cancelled' | 'completed' | 'all') => {
    this.setLoading(true);
    this.setError(null);

    if (!this.selectedCollaboratorId) {
      this.setLoading(false);
      return;
    }

    const selectedCompanyId = companyStore.getIdCompanySelectedDefault;

    const profileExists = professionalStore.getProfileProfessional !== null;
    const profileHasId = profileExists && professionalStore.getProfileProfessional!.id !== undefined;

    if (!selectedCompanyId || !profileHasId) {
      return;
    }

    
    this.collaboratorId = professionalStore.getProfileProfessional!.id!;

    // Calculer le filtre de dates basé sur le mode et la date sélectionnée
    const dateFilter = this.getDateRangeFilter();

    try {
        const response = await this.bookingModel.getProfessionalBookings(dateFilter, status, selectedCompanyId,
            this.selectedCollaboratorId);

        if (response.status === HTTP_STATUS_CODE.OK) {
          const bookings = response.data as BookingListItem[] || [];
          bookingStore.setBookings(bookings);
        } else {
          toastStore.error(response.message);
        }

    } catch (error: any) {
      toastStore.error((error?.response?.data?.message));
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Récupère les détails d'une réservation par son ID
   */
  fetchBookingById = async (id: string) => {
    this.setLoading(true);
    this.setError(null);
    
    try {
      const response = await this.bookingModel.getBookingById(id);
      
      if (response.success && response.data) {
        bookingStore.setSelectedBooking(response.data as BookingDetail);
        return true;
      } else {
        const errorMsg = response.message || 'Erreur lors de la récupération des détails de la réservation';
        this.setError(errorMsg);
        toastStore.error(errorMsg);
        return false;
      }
    } catch (error) {
      const errorMsg = 'Erreur lors de la récupération des détails de la réservation';
      this.setError(errorMsg);
      toastStore.error(errorMsg);
      return false;
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Crée une nouvelle réservation
   */
  createBooking = async (params: BookingCreateParams) => {
    this.setLoading(true);
    this.setError(null);

    try {
      if (params.serviceId && params.startTime && params.endTime) {

        const isAvailable = this.checkAvailability(params.startTime, params.endTime);
        if (!isAvailable) {
          const errorMsg = 'Cette plage horaire n\'est pas disponible';
          this.setError(errorMsg);
          toastStore.error(errorMsg);
          return false;
        }
      }

      const response = await this.bookingModel.createBooking(params);

      if (response.success) {
        toastStore.success(response.message);
        await this.calendarViewModel.fetchProfessionalBookings()
        await this.dashboardViewModel.initializeAppointments()
        return true;
      } else {
        toastStore.error(response.message);
        return false;
      }
    } catch (error: any) {
      toastStore.error(error?.response?.data?.message);
      return false;
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Crée une réservation manuelle (par le professionnel)
   * Permet de créer des réservations en dehors des créneaux disponibles
   */
  createManualBooking = async (params: BookingCreateParams) => {
    this.setLoading(true);
    this.setError(null);

    try {
      const response = await this.bookingModel.createManualBooking(params);

      if (response.success) {
        toastStore.success(response.message);
        await this.calendarViewModel.fetchProfessionalBookings()
        await this.dashboardViewModel.initializeAppointments()
        return true;
      } else {
        toastStore.error(response.message);
        return false;
      }
    } catch (error: any) {
      toastStore.error(error?.response?.data?.message);
      return false;
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Met à jour une réservation manuelle
   */
  updateManualBooking = async (id: string, params: BookingCreateParams) => {
    this.setLoading(true);
    this.setError(null);

    try {
      const response = await this.bookingModel.updateManualBooking(id, params);

      if (response.success) {
        toastStore.success(response.message);
        await this.calendarViewModel.fetchProfessionalBookings()
        await this.dashboardViewModel.initializeAppointments()
        return true;
      } else {
        toastStore.error(response.message);
        return false;
      }
    } catch (error: any) {
      toastStore.error(error?.response?.data?.message);
      return false;
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Vérifie si une plage horaire est disponible dans les disponibilités
   * @param startTime Heure de début au format ISO
   * @param endTime Heure de fin au format ISO
   * @returns true si la plage horaire est disponible, false sinon
   */
  private checkAvailability(startTime: string, endTime: string): boolean {
    if (availabilityStore.getAvailability.length === 0) {
      return true;
    }
    
    // Convertir les dates UTC en dates dans le fuseau horaire du professionnel
    const start = utcToLocalTime(startTime);
    const end = utcToLocalTime(endTime);
    const day = start.getDay();
    const startDate = formatDateInTimeZone(start, 'yyyy-MM-dd');
    
    // Extraire les heures et minutes dans le fuseau horaire du professionnel en utilisant formatTimeInTimeZone
    const startTimeStr = formatTimeInTimeZone(start, 'HH:mm');
    const endTimeStr = formatTimeInTimeZone(end, 'HH:mm');


    return availabilityStore.getAvailability.some((availability: Availability) => {
      if (availability.recurrence) {
        if (availability.day) {
          const availabilityDay = parseInt(availability.day);
          if (availabilityDay !== day) {
            return false;
          }
        }

        const recurrenceStart = new Date(availability.recurrence.startDate);
        const recurrenceEnd = new Date(availability.recurrence.endDate);
        if (start < recurrenceStart || start > recurrenceEnd) {
          return false;
        }
      } else {
        // Pour les disponibilités ponctuelles, vérifier la date
        if (availability.startDate !== startDate) {
          return false;
        }
      }

      return startTimeStr >= availability.startTime && endTimeStr <= availability.endTime;
    });
  }

  /**
   * Bascule la sélection d'une réservation
   */
  toggleReservation = (id: string) => {
    const index = this.selectedReservations.indexOf(id);
    if (index === -1) {
      // Créer une nouvelle copie du tableau avec l'ID ajouté
      const updatedReservations = [...this.selectedReservations, id];
      this.setSelectedReservations(updatedReservations);
    } else {
      const updatedReservations = this.selectedReservations.filter(reservationId => reservationId !== id);
      this.setSelectedReservations(updatedReservations);
    }
  }

  /**
   * Calcule les statistiques des réservations filtrées
   */
  calculateStats = (reservations: any[]) => {
    if (!reservations || reservations.length === 0) {
      return {
        total: 0,
        confirmed: 0,
        canceled: 0,
        totalValue: 0,
        confirmationRate: 0
      };
    }

    // Déterminer si nous travaillons avec des BookingListItem ou des Reservation
    const isBookingListItem = 'totalPrice' in reservations[0];

    return {
      total: reservations.length,
      confirmed: reservations.filter(r =>
        isBookingListItem
          ? r.status === 'confirmed'
          : r.status === 'confirmed'
      ).length,
      canceled: reservations.filter(r =>
        isBookingListItem
          ? r.status === 'cancelled'
          : r.status === 'canceled'
      ).length,
      totalValue: reservations
        .filter(r => isBookingListItem
          ? r.status !== 'cancelled'
          : r.status !== 'canceled'
        )
        .reduce((sum, r) => {
          if (isBookingListItem) {
            const price = typeof r.totalPrice === 'number' ? r.totalPrice : parseFloat(r.totalPrice || '0');
            return sum + (isNaN(price) ? 0 : price);
          } else {
            // Pour les anciennes données au format string
            const priceStr = r.price?.replace(/[^0-9.,]/g, '') || '0';
            const price = parseFloat(priceStr.replace(',', '.'));
            return sum + (isNaN(price) ? 0 : price);
          }
        }, 0),
      confirmationRate: Math.round(
        (reservations.filter(r =>
          isBookingListItem
            ? r.status === 'confirmed'
            : r.status === 'confirmed'
        ).length /
        (reservations.length || 1)) * 100
      )
    };
  }

  /**
   * Retourne les informations d'affichage pour un statut de réservation
   * @param status - Le statut de la réservation
   * @returns Un objet contenant l'icône, la couleur et le label
   */
  getStatusInfo = (status: BookingStatus | 'canceled') => {
    let uiStatus = status;
    if (status === 'cancelled') {
      uiStatus = 'canceled' as BookingStatus | 'canceled';
    }

    switch (uiStatus) {
      case 'confirmed':
        return {
          color: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300',
          label: 'Confirmé',
          iconType: 'check' as const
        };
      case 'canceled':
      case 'cancelled':
        return {
          color: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
          label: 'Annulé',
          iconType: 'xCircle' as const
        };
      case 'completed':
        return {
          color: 'bg-gray-100 text-gray-800 dark:bg-gray-900/40 dark:text-gray-300',
          label: 'Terminé',
          iconType: 'check' as const
        };
      default:
        return {
          color: 'bg-gray-100 text-gray-800 dark:bg-gray-900/40 dark:text-gray-300',
          label: uiStatus,
          iconType: null
        };
    }
  }

  /**
   * Formate une date au format spécifié
   * @param dateString - La date à formater
   * @param formatStr - Le format de sortie (par défaut: 'EEEE d MMMM yyyy')
   * @returns La date formatée ou la chaîne originale en cas d'erreur
   */
  formatDate = (dateString: string, formatStr: string = 'EEEE d MMMM yyyy'): string => {
    if (!dateString) return '';
    try {
      return formatDateInTimeZone(dateString, formatStr);
    } catch (error) {
      console.error('Erreur de formatage de date:', error, dateString);
      return dateString;
    }
  }

  /**
   * Formate une heure au format spécifié
   * @param dateString - La date/heure à formater
   * @param formatStr - Le format de sortie (par défaut: 'HH:mm')
   * @returns L'heure formatée ou la chaîne originale en cas d'erreur
   */
  formatTime = (dateString: string, formatStr: string = 'HH:mm'): string => {
    if (!dateString) return '';
    try {
      return formatTimeInTimeZone(dateString, formatStr);
    } catch (error) {
      console.error('Erreur de formatage d\'heure:', error, dateString);
      return dateString;
    }
  }

  /**
   * Récupère les détails complets d'une réservation avec calcul du prix total
   * @param id - L'ID de la réservation
   * @returns Les détails de la réservation ou null en cas d'erreur
   */
  getReservationDetails = async (id: string): Promise<BookingDetail | null> => {
    try {
      const success = await this.fetchBookingById(id);

      if (!success || !bookingStore.selectedBooking) return null;

      const booking = bookingStore.selectedBooking;

      return booking;
    } catch (error) {
      console.error('Erreur lors de la récupération des détails de la réservation:', error);
      return null;
    }
  }

  /**
   * Exporte le chiffre d'affaires de l'entreprise ou d'un collaborateur
   * @param startDate - Date de début (YYYY-MM-DD)
   * @param endDate - Date de fin (YYYY-MM-DD)
   * @param companyId - ID de l'entreprise
   * @param format - Format d'export ('csv' ou 'pdf')
   * @param collaboratorId - ID du collaborateur (optionnel)
   */
  exportRevenue = async (
    startDate: string,
    endDate: string,
    companyId: string,
    format: 'csv' | 'pdf',
    collaboratorId?: string
  ): Promise<boolean> => {
    this.setLoading(true);
    this.setError(null);

    try {
      await this.bookingModel.exportCompanyRevenue(
        startDate,
        endDate,
        companyId,
        format,
        collaboratorId
      );

      toastStore.success('Export réussi ! Le fichier a été téléchargé.');
      return true;
    } catch (error: any) {
      const errorMessage = error?.message || 'Erreur lors de l\'export du chiffre d\'affaires';
      this.setError(errorMessage);
      toastStore.error(errorMessage);
      return false;
    } finally {
      this.setLoading(false);
    }
  }
}

export const bookingViewModelMobX = new BookingViewModelMobX();
